import React from 'react';
import { addDays, isSameDay, startOfDay } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Surface, Text } from '@/components/flup';
import ScrollRail from './ScrollRail';

/**
 * The date rail.
 *
 * A vendor's day almost always starts on today or tomorrow, so those two sit
 * first and need no interaction at all. The calendar popover is still there for
 * "someone wants the 14th of next month", but it is deliberately the secondary
 * path — the screen this replaced made a date popover the *only* path, which
 * cost two clicks on every single visit.
 */

const DAYS_AHEAD = 21;

interface DayBarProps {
  value: Date;
  onChange: (date: Date) => void;
  /** Rendered without a card of its own, for hosting inside another one. */
  bare?: boolean;
}

const DayCard: React.FC<{ date: Date; selected: boolean; onClick: () => void }> = React.memo(
  ({ date, selected, onClick }) => {
    const today = startOfDay(new Date());
    const isToday = isSameDay(date, today);
    const isTomorrow = isSameDay(date, addDays(today, 1));
    const badge = isToday ? 'TODAY' : isTomorrow ? 'TMRW' : null;

    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(
          'flex flex-col items-center justify-center gap-0.5 w-[62px] shrink-0 py-2.5 rounded-flup-md border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-2',
          selected
            ? 'bg-flup-green-soft border-flup-green'
            : 'bg-flup-surface border-flup-hairline hover:border-flup-hairline-2 hover:bg-flup-surface-alt',
        )}
      >
        <Text variant="micro" tone={selected ? 'green' : 'tertiary'} as="span">
          {date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
        </Text>
        <Text
          variant="statSm"
          as="span"
          className={selected ? 'text-flup-green-ink' : 'text-flup-ink'}
        >
          {String(date.getDate()).padStart(2, '0')}
        </Text>
        <Text variant="micro" tone={selected ? 'green' : 'tertiary'} as="span">
          {badge || date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
        </Text>
      </button>
    );
  },
);

DayCard.displayName = 'DayCard';

const DayBar: React.FC<DayBarProps> = ({ value, onChange, bare = false }) => {
  const today = startOfDay(new Date());
  const dates = React.useMemo(
    () => Array.from({ length: DAYS_AHEAD }, (_, i) => addDays(today, i)),
    // A new day should rebuild the rail; the timestamp inside `today` should not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today.getTime()],
  );

  // The rail and its controls share one line. A label row above them would say
  // "Date" over a row of dates, and cost a line of slots to do it.
  const body = (
    <div className="flex items-center gap-3">
      <ScrollRail className="gap-2" outerClassName="flex-1">
        {dates.map((date) => (
          <DayCard
            key={date.toISOString()}
            date={date}
            selected={isSameDay(date, value)}
            onClick={() => onChange(date)}
          />
        ))}
      </ScrollRail>

      <div className="flex items-center gap-1.5 shrink-0">
        {!isSameDay(value, today) ? (
          <button
            type="button"
            onClick={() => onChange(today)}
            className="h-8 px-3 rounded-flup-xs bg-flup-surface-alt text-flup-ink-2 hover:bg-flup-sunken hover:text-flup-ink transition-colors"
          >
            <Text variant="micro" tone="inherit" as="span">
              Today
            </Text>
          </button>
        ) : null}

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Pick a date"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-flup-xs border border-flup-hairline bg-flup-surface text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
            >
              <CalendarDays className="w-3.5 h-3.5" aria-hidden />
              <Text variant="micro" tone="inherit" as="span" className="hidden sm:inline">
                Pick date
              </Text>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={value}
              onSelect={(date) => date && onChange(startOfDay(date))}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return bare ? body : <Surface padding="p-4 sm:p-5">{body}</Surface>;
};

export default React.memo(DayBar);
